/**
 * ContentFooterSlot - Renders controls in the content-footer location.
 *
 * This slot appears below the main content area and is typically used for
 * controls that don't need to be visible while browsing (like axis range controls).
 *
 * Two distinct rendering modes:
 * 1. FlexboxContentFooter (fullscreen): Complex priority-based flexbox layout
 * 2. StaticControlRow (non-fullscreen): Static, comma-separated text in a single line
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {ControlRenderer} from '../renderers/ControlRenderer';
import {type GridData, type SlotRegistry} from '../types';
import {createControlGroups, getControlStyles} from '../utils/controlLayout';
import {StaticControlRow} from './StaticControlRow';

export type ContentFooterSlotProps = {
  controls: NonNullable<SlotRegistry[keyof SlotRegistry]>;
  config: MediaConfigWithDefaults;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  stepMeta: StepMeta;
  mediaSliderKey: string;
  mediaType: MediaCardType;
  isMaxStepLoading: boolean;
  mediaBrowserSize: {width: number; height: number};
  gridData: GridData;
  contentFooterWidth?: number;
  isFullScreenPanelPage?: boolean;
};

/**
 * Renders controls in the content-footer location with two distinct layouts:
 * 1. Fullscreen: Complex flexbox layout with priority-based growth/shrink
 * 2. Non-fullscreen: Static text row with comma-separated controls for compact display
 */
export const ContentFooterSlot: React.FC<ContentFooterSlotProps> = props => {
  if (props.controls.length === 0) {
    return null;
  }

  // Split into two completely different rendering paths for clarity
  if (props.isFullScreenPanelPage) {
    return <FlexboxContentFooter {...props} />;
  } else {
    return (
      <StaticControlRow
        {...props}
        isFullScreenPanelPage={false}
        keyPrefix="footer"
      />
    );
  }
};

/**
 * Complex flexbox layout for fullscreen mode.
 * Uses priority-based growth/shrink with responsive wrapping.
 * - Run: min 180px, grows with priority 9
 * - MediaKey: min 140px, grows with priority 7
 * - Step+Index: min 120px combined, grows with priority 6
 */
const FlexboxContentFooter: React.FC<ContentFooterSlotProps> = ({
  controls,
  config,
  updateConfig,
  stepMeta,
  isMaxStepLoading,
  mediaBrowserSize,
  gridData,
  contentFooterWidth,
}) => {
  const controlGroups = createControlGroups(controls);
  const isCompareMode = config.mode === 'compare';
  const gapClass = isCompareMode ? 'gap-8' : 'gap-16';

  const containerClasses = twMerge(
    'w-full', // Allow full expansion in fullscreen
    'flex flex-1',
    isCompareMode ? 'flex-nowrap' : 'flex-wrap',
    'max-w-full',
    'items-start justify-center',
    'text-wb-text-primary',
    'pt-8',
    gapClass
  );

  return (
    <div className={containerClasses}>
      {controlGroups.map((group, groupIdx) => {
        const groupStyles = getControlStyles(group.name);
        const isStepIndexGroup = group.name === 'stepIndex';
        const hasStepOrIndex =
          isStepIndexGroup || group.name === 'step' || group.name === 'index';
        // In gallery/grid, allow step/index groups to wrap so sliders stack when narrow (like workspace preview)
        const allowGroupWrap = hasStepOrIndex && !isCompareMode;

        const groupClasses = twMerge(
          `footer-${group.name}-${groupIdx}`,
          'flex items-center justify-center',
          gapClass,
          allowGroupWrap && 'flex-wrap',
          groupStyles.flexGrow,
          groupStyles.minWidth,
          allowGroupWrap && 'min-w-0',
          'basis-0 shrink-0',
          isStepIndexGroup && 'shrink-1',
          hasStepOrIndex && !isCompareMode && 'max-w-[700px]'
        );

        return (
          <div
            // oxlint-disable-next-line react/no-array-index-key -- https://oxc.rs/docs/guide/usage/linter/rules/react/no-array-index-key.html
            key={`footer-${group.name}-${groupIdx}`}
            className={groupClasses}>
            {group.controls.map(control => (
              <React.Fragment key={`footer-group-${control!.control}`}>
                <ControlRenderer
                  control={control!}
                  index={controls.indexOf(control!)}
                  config={config}
                  stepMeta={stepMeta}
                  updateConfig={updateConfig}
                  isMaxStepLoading={isMaxStepLoading}
                  mediaBrowserSize={mediaBrowserSize}
                  gridData={gridData}
                  contentFooterWidth={contentFooterWidth}
                  isFullScreenPanelPage={true}
                  layout="horizontal"
                />
              </React.Fragment>
            ))}
          </div>
        );
      })}
    </div>
  );
};
